import { AlertTriangle, Sparkles, Clock, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useUserRole } from "@/hooks/useUserRole";
import { useSubscription } from "@/hooks/useSubscription";
import { cn } from "@/lib/utils";

/**
 * Persistent banner shown in the dashboard header area:
 *  - Trial users see how many days remain and a link to manage their plan.
 *  - Expired users see a read-only warning and a strong resubscribe CTA.
 *  - Everyone else sees nothing.
 */
export const TrialBanner = () => {
  const navigate = useNavigate();
  const { role, isTrial, isExpired, trialDaysRemaining, trialEndsAt, loading } = useUserRole();
  const { openCustomerPortal } = useSubscription();

  if (loading || !role) return null;

  // Never started a trial (website signup that skipped checkout).
  if (isExpired && !trialEndsAt) {
    return (
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 rounded-lg border border-primary/40 bg-primary/10">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-primary/15 p-2 shrink-0">
            <Sparkles className="h-4 w-4 text-primary" />
          </div>
          <div>
            <p className="font-semibold text-sm">Start your 7-day free trial</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Add a card to unlock EchoCRM. You won't be charged until the trial ends, cancel anytime.
            </p>
          </div>
        </div>
        <Button size="sm" className="self-end sm:self-auto" onClick={() => navigate("/pricing")}>
          <Sparkles className="h-3.5 w-3.5 mr-1.5" />
          Start free trial
        </Button>
      </div>
    );
  }

  if (isExpired) {
    return (
      <div
        className={cn(
          "flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 rounded-lg",
          "border border-destructive/40 bg-destructive/10"
        )}
      >
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-destructive/15 p-2 shrink-0">
            <Lock className="h-4 w-4 text-destructive" />
          </div>
          <div>
            <p className="font-semibold text-sm text-destructive">Your account is read-only</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Your subscription has ended. Your data is safe — resubscribe to
              start editing again.
            </p>
          </div>
        </div>
        <div className="flex gap-2 self-end sm:self-auto">
          <Button variant="outline" size="sm" onClick={openCustomerPortal}>
            Manage billing
          </Button>
          <Button size="sm" onClick={() => navigate("/pricing")}>
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />
            Resubscribe
          </Button>
        </div>
      </div>
    );
  }

  if (isTrial) {
    const days = trialDaysRemaining ?? 7;
    const critical = days <= 3;
    return (
      <div
        className={cn(
          "flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 rounded-lg border",
          critical
            ? "border-amber-500/40 bg-amber-500/10"
            : "border-primary/30 bg-primary/5"
        )}
      >
        <div className="flex items-start gap-3">
          <div
            className={cn(
              "rounded-full p-2 shrink-0",
              critical ? "bg-amber-500/15" : "bg-primary/10"
            )}
          >
            {critical ? (
              <AlertTriangle className="h-4 w-4 text-amber-600" />
            ) : (
              <Clock className="h-4 w-4 text-primary" />
            )}
          </div>
          <div>
            <p className="font-semibold text-sm">
              {days === 0
                ? "Your trial ends today"
                : `${days} day${days === 1 ? "" : "s"} left in your free trial`}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Your Pro subscription starts when the trial ends. Cancel anytime
              before then from Manage billing.
            </p>
          </div>
        </div>
        <div className="flex gap-2 self-end sm:self-auto">
          <Button variant="outline" size="sm" onClick={openCustomerPortal}>
            Manage billing
          </Button>
          <Button size="sm" onClick={() => navigate("/pricing")}>
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />
            Upgrade to Business
          </Button>
        </div>
      </div>
    );
  }

  return null;
};